import { ButtonLink, LoadingText } from "@naf/teamscheme";
import type React from "react";
import { createContext, useSyncExternalStore } from "react";
import { ConnectionStatus } from "./ConnectionStatus";
import signalRManager from "./SignalRManager";
import BottomPanel from "../shared/layout/BottomPanel";

interface ISignalRClientContext {
  status: ConnectionStatus;
}
const SignalRClientContext = createContext<ISignalRClientContext | null>(null);

function getStatusText(status: ConnectionStatus) {
  switch (status) {
    case ConnectionStatus.Connecting:
      return <LoadingText text="Kobler til" />;
    case ConnectionStatus.Reconnecting:
      return <LoadingText text="Kobler til på nytt" />;
    case ConnectionStatus.Connected:
      return "Tilkoblet";
    case ConnectionStatus.Disconnected:
      return "Frakoblet";
    default:
      throw new Error(`Unrecognized status ${status}!`);
  }
}

if (typeof window !== "undefined") {
  signalRManager.connect();
}

const store = {
  subscribe(listener: () => void) {
    return signalRManager.subscribeToStatus(listener);
  },
  getSnapshot() {
    return signalRManager.status;
  },
  getServerSnapshot() {
    return signalRManager.status;
  },
};

export function SignalRClientProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const status = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getServerSnapshot,
  );

  return (
    <SignalRClientContext.Provider value={{ status }}>
      {children}
      {[
        ConnectionStatus.Connecting,
        ConnectionStatus.Reconnecting,
        ConnectionStatus.Disconnected,
      ].includes(status) ? (
        <BottomPanel>
          <span>{getStatusText(status)}</span>
          {status === ConnectionStatus.Disconnected ? (
            <ButtonLink
              variant="secondary"
              onClick={() => signalRManager.connect()}
            >
              Koble til på nytt
            </ButtonLink>
          ) : null}
        </BottomPanel>
      ) : null}
      {status === ConnectionStatus.Failed ? (
        <BottomPanel>
          <span>
            Kunne ikke opprette tilkobling mot serveren. Du må laste siden på
            nytt for å se endringer.
          </span>
        </BottomPanel>
      ) : null}
    </SignalRClientContext.Provider>
  );
}
